import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';

import PoweredByFleetOps from 'components/PoweredByFleetOps';
import { LeaderboardStep } from 'components/DriverLeaderboard/buildSteps';
import WallboardPrintProvider from 'contextProviders/WallboardPrintProvider';
import DashboardsContext from 'contexts/DashboardsContext';
import WallboardContext from 'contexts/WallboardContext';
import WallboardsContext from 'contexts/WallboardsContext';
import usePrintReady from 'hooks/usePrintReady';
import { PRINT_WALLBOARD_PREFIX } from 'navigation/appRoutes';
import resolveWallboardSlides from 'screens/WallBoard/resolveWallboardSlides';
import {
  PoweredByPosition,
  WallboardDashboard,
} from 'screens/WallBoard/WallBoard';

import collectVideoOverlays, {
  collectProgressOverlay,
} from './collectVideoOverlays';
import getPrintSlideKind from './getPrintSlideKind';

// The renderer captures at 1920×1080, which is how a non-Fire TV shows a
// wallboard today: its zoom defaults to 2 (screens/WallBoard).
const DEFAULT_DASHBOARD_ZOOM = 2;

const PrintGlobalStyle = createGlobalStyle`
  body { margin: 0; overflow: hidden; background: var(--color-bg-primary); }
`;

const PrintRoot = styled.div`
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background: var(--color-bg-primary);
`;

interface PrintTarget {
  slideId: string;
  stepIndex: number;
}

/**
 * Chrome-less wallboard slide, for the wallboard screenshot renderer
 * (tools/report-renderer/src/renderWallboard.ts). The page loads once per
 * wallboard; the renderer then shows each slide (and each driver leaderboard
 * step) in turn through window.__fleetopsWallboardPrint.show, waits for
 * window.__fleetopsWallboardPrint.current to report that target, and takes the
 * screenshot. Weather slides aren't captured: the TV plays them live.
 */
const PrintWallboardShow = () => {
  const { pathname } = useLocation();
  const wallBoardId = pathname
    .split(PRINT_WALLBOARD_PREFIX)[1]
    ?.split(/[?/]/)[0];
  const { wallBoards, isLoading: isWallboardsLoading } =
    useContext(WallboardsContext);
  const { allDashboards, isLoading: isDashboardsLoading } =
    useContext(DashboardsContext);
  const isLoading = isWallboardsLoading || isDashboardsLoading;

  const wallboard = useMemo(
    () => wallBoards.find((w) => w.id === wallBoardId),
    [wallBoardId, wallBoards],
  );
  const slides = useMemo(
    () =>
      wallboard ? resolveWallboardSlides({ wallboard, allDashboards }) : [],
    [allDashboards, wallboard],
  );

  const [target, setTarget] = useState<PrintTarget | undefined>();
  const [resolvedSteps, setResolvedSteps] = useState<
    { slideId: string; steps: LeaderboardStep[] } | undefined
  >();
  const slide = target
    ? slides.find((s) => s.id === target.slideId)
    : undefined;
  const kind = getPrintSlideKind(slide);
  const steps =
    target && resolvedSteps?.slideId === target.slideId
      ? resolvedSteps.steps
      : undefined;
  const key = target ? `${target.slideId}:${target.stepIndex}` : '';

  const isReady = usePrintReady({
    isContentReady:
      !isLoading && !!target && (kind !== 'leaderboard' || !!steps),
    resetKey: key,
  });

  const onLeaderboardStepsResolved = useCallback(
    (newSteps: LeaderboardStep[]) => {
      if (target) {
        setResolvedSteps({ slideId: target.slideId, steps: newSteps });
      }
    },
    [target],
  );

  useEffect(() => {
    if (isLoading || !wallboard) {
      window.__fleetopsWallboardPrint = undefined;
      return;
    }

    window.__fleetopsWallboardPrint = {
      wallboardId: wallboard.id,
      intervalTimeSeconds: wallboard.intervalTimeSeconds,
      weatherMapZoom: wallboard.weatherMapZoom,
      slides: slides.map((s) => ({
        slideId: s.id,
        kind: getPrintSlideKind(s),
      })),
      show: (newTarget) => {
        setTarget({
          slideId: newTarget.slideId,
          stepIndex: newTarget.stepIndex,
        });
      },
    };
    return () => {
      window.__fleetopsWallboardPrint = undefined;
    };
  }, [isLoading, slides, wallboard]);

  useEffect(() => {
    const api = window.__fleetopsWallboardPrint;
    if (!api) {
      return;
    }
    if (!isReady || !target) {
      api.current = undefined;
      return;
    }
    api.current = {
      key,
      slideId: target.slideId,
      stepIndex: target.stepIndex,
      kind,
      steps: steps?.map((s) => ({ dwellSeconds: s.dwellSeconds })),
      videos: collectVideoOverlays(),
      progress: collectProgressOverlay(),
    };
  }, [isReady, key, kind, steps, target]);

  if (!wallboard) {
    return null;
  }

  return (
    <PrintRoot
      id="print-wallboard-root"
      data-print-ready={isReady ? 'true' : undefined}
      data-print-key={key}
    >
      <PrintGlobalStyle />
      <WallboardPrintProvider>
        <WallboardContext.Provider
          value={{
            wallboard,
            isWallboard: true,
            weatherMapZoom: wallboard.weatherMapZoom,
            dashboardZoom: wallboard.dashboardZoom ?? DEFAULT_DASHBOARD_ZOOM,
          }}
        >
          <>
            {slide && target && kind !== 'weather' && (
              <WallboardDashboard
                dashboard={slide}
                key={slide.id}
                leaderboardStepIndex={target.stepIndex}
                onLeaderboardStepsResolved={onLeaderboardStepsResolved}
              />
            )}
            {/* The Driver Leaderboard renders the mark in its own footer. */}
            {kind === 'dashboard' && (
              <PoweredByPosition>
                <PoweredByFleetOps />
              </PoweredByPosition>
            )}
          </>
        </WallboardContext.Provider>
      </WallboardPrintProvider>
    </PrintRoot>
  );
};

export default PrintWallboardShow;
